import React from "react";
import {Chip, Typography} from "@mui/material";
import {Blue3, Grey6} from "../theme/styles";

// THE lifecycle status palette, matching cres-t1/src/util/statusPill and cres-t5's. One definition
// per app, and the same definition in each, because a status must not mean one colour in QMS and
// another in Docs -- it is the same revision of the same document, seen from two applications, and
// people move between the two screens constantly.
//
// FILLED, NOT OUTLINED, matching the suite: it reads at a glance in a dense grid, which is where
// most statuses are seen.
//
// The colours carry meaning and are not arbitrary:
//   RELEASED    green  -- live and current
//   DRAFT       blue   -- being worked on, not yet live. THE ONLY STATE THIS APP CAN EDIT.
//   DEPRECATED  amber  -- STILL LIVE. It resolves for anything already pointing at it and is only
//                         kept out of pickers, so it is not red. This is the one people get wrong.
//   SUPERSEDED  grey   -- frozen, replaced by a newer revision
//   CANCELLED   grey   -- frozen, abandoned before release
//   VOID        red    -- out of service. The terminal state, and the only red one.
//
// NO CHANGE ORDER STATES, deliberately: t1 carries IN_REVIEW, CLOSED, VOIDED, IMPLEMENTATION and
// PENDING_CHANGE because t1 has change orders. Docs has none, so defining them here would be a
// palette entry for something that cannot appear -- and the next reader would reasonably assume it
// can. PENDING_CHANGE is the near miss: a document IN a draft change order shows that status in
// QMS, but it reaches Docs as DRAFT because that is what its revision is.
const PALETTE: Record<string, {bg: string; fg: string}> = {
    RELEASED:   {bg: "rgba(46,204,113,0.16)",  fg: "#1E9E5A"},
    DRAFT:      {bg: "rgba(20,152,216,0.14)",  fg: Blue3},
    SUPERSEDED: {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    DEPRECATED: {bg: "rgba(241,196,15,0.20)",  fg: "#8A5B00"},
    VOID:       {bg: "rgba(231,76,60,0.14)",   fg: "#D64535"},
    CANCELLED:  {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    ARCHIVED:   {bg: "rgba(123,130,145,0.16)", fg: Grey6},   // legacy rows, until they are migrated
};

const UNKNOWN = {bg: "rgba(123,130,145,0.16)", fg: Grey6};

export function statusColours(status?: string | null) {
    return (status && PALETTE[status]) || UNKNOWN;
}

/**
 * Just the colour, for somewhere a whole chip will not fit.
 *
 * THE FILL HUE, NOT THE TEXT COLOUR, and the difference is visible. A chip is a pale tint of the
 * hue with a DARKENED version of it for the label -- so RELEASED reads as light green on screen
 * while its `fg` is a dark forest green. A dot painted `fg` is from the right palette entry and
 * still looks like a different colour from the chip beside it.
 */
export function statusColor(status?: string | null): string {
    if (!status) return Grey6;
    const entry = PALETTE[String(status).toUpperCase()];
    if (!entry) return Grey6;

    const match = entry.bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    return match ? `rgb(${match[1]}, ${match[2]}, ${match[3]})` : entry.fg;
}

/** The status as a coloured pill, or an em dash when there is no status. */
export function statusPill(status?: string | null) {
    if (!status) return <Typography style={{fontSize: 14, color: Grey6}}>—</Typography>;

    const c = statusColours(status);
    // THE STORED VALUE IS NOT THE LABEL. Statuses are stored as codes, and some carry an
    // underscore. Printing the code puts a piece of the database on screen; the palette is keyed on
    // the code, so only the text shown is changed here.
    const label = String(status).replace(/_/g, " ");
    return <Chip label={label} size="small"
                 sx={{fontSize: 11, height: 22, minWidth: 100, fontWeight: 600,
                      backgroundColor: c.bg, color: c.fg, "& .MuiChip-label": {px: 1}}}/>;
}
